// Hansero Chat Web Push client helper
// 알림 재정비 원칙:
// - 프론트는 구독 등록/해제/읽음 알림만 담당
// - 앱 포커스만으로 전체 OS 알림/배지를 지우지 않음
// - 방을 실제로 읽었을 때 해당 방 알림만 정리

const CHAT_SW_URL = "/chat-sw.js";
const PUSH_API_BASE = "/chat-api/push";

let lastAutoSubscribeAt = 0;

function getStoredToken() {
  const keys = ["token", "accessToken", "access_token", "chat_token", "hansero_token", "groupware_token"];
  for (const key of keys) {
    try {
      const value = window.localStorage.getItem(key) || window.sessionStorage.getItem(key);
      if (value) return value;
    } catch (err) {}
  }
  return "";
}

function buildAuthHeaders(token) {
  const value = token || getStoredToken();
  return value ? { Authorization: `Bearer ${value}` } : {};
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

function getDeviceLabel() {
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  if (/iphone|ipad|ipod/i.test(ua)) return `iOS PWA ${platform}`.trim();
  if (/android/i.test(ua)) return `Android ${platform}`.trim();
  if (/windows/i.test(ua)) return `Windows ${platform}`.trim();
  if (/mac/i.test(ua)) return `Mac ${platform}`.trim();
  return ua.slice(0, 120) || "Unknown device";
}

export function isPushSupported() {
  return Boolean(typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window);
}

export async function registerChatServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.register(CHAT_SW_URL, { updateViaCache: "none" });
  try { await registration.update(); } catch (err) {}
  await navigator.serviceWorker.ready;
  return registration;
}

export async function clearChatNotifications({ roomId = null, all = false, badgeCount = null } = {}) {
  const hasBadgeCount = badgeCount !== null && badgeCount !== undefined && Number.isFinite(Number(badgeCount));
  const nextBadgeCount = hasBadgeCount ? Math.max(0, Number(badgeCount) || 0) : null;

  try {
    if (hasBadgeCount) {
      if (nextBadgeCount > 0 && "setAppBadge" in navigator) await navigator.setAppBadge(nextBadgeCount);
      else if (nextBadgeCount <= 0 && "clearAppBadge" in navigator) await navigator.clearAppBadge();
    }
  } catch (err) {}

  try {
    const registration = await navigator.serviceWorker.ready;
    const message = all || !roomId
      ? { type: "CHAT_CLEAR_ALL_NOTIFICATIONS", badge_count: nextBadgeCount }
      : { type: "CHAT_ROOM_READ", room_id: roomId, badge_count: nextBadgeCount };
    if (registration.active) registration.active.postMessage(message);
    if (navigator.serviceWorker.controller) navigator.serviceWorker.controller.postMessage(message);
  } catch (err) {}
}

export async function setChatAppBadge(count = 0) {
  const n = Math.max(0, Number(count) || 0);
  try {
    if (n <= 0 && "clearAppBadge" in navigator) await navigator.clearAppBadge();
    else if (n > 0 && "setAppBadge" in navigator) await navigator.setAppBadge(n);
  } catch (err) {}

  try {
    const registration = await navigator.serviceWorker.ready;
    const message = n <= 0 ? { type: "CHAT_CLEAR_BADGE" } : { type: "CHAT_SET_BADGE", count: n };
    if (registration.active) registration.active.postMessage(message);
    if (navigator.serviceWorker.controller) navigator.serviceWorker.controller.postMessage(message);
  } catch (err) {}
}

async function fetchJson(path, { token, method = "GET", body } = {}) {
  const response = await fetch(`${PUSH_API_BASE}${path}`, {
    method,
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...buildAuthHeaders(token),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await response.json(); } catch (err) {}
  if (!response.ok) {
    const error = new Error(data?.error || data?.message || `HTTP_${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

export async function ensureDevicePushSubscription({ token } = {}) {
  if (!isPushSupported()) return { ok: false, reason: "PUSH_NOT_SUPPORTED" };
  if (Notification.permission === "denied") return { ok: false, reason: "NOTIFICATION_DENIED" };
  if (Notification.permission !== "granted") return { ok: false, reason: "NOTIFICATION_PERMISSION_NOT_GRANTED" };

  const registration = await registerChatServiceWorker();
  const publicKeyJson = await fetchJson("/public-key", { token });
  const publicKey = publicKeyJson.publicKey || publicKeyJson.public_key;
  if (!publicKey) return { ok: false, reason: "PUBLIC_KEY_EMPTY" };

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const registered = await fetchJson("/subscriptions", {
    token,
    method: "POST",
    body: { subscription, device_label: getDeviceLabel(), user_agent: navigator.userAgent || "" },
  });

  const binding = { type:'CHAT_SET_CURRENT_EMPLOYEE', employee_id:Number(registered.employee_id)||0 };
  try { registration.active?.postMessage(binding); } catch (_) {}
  try { navigator.serviceWorker.controller?.postMessage(binding); } catch (_) {}
  return { ok: true, subscription };
}

export async function requestAndEnsureDevicePushSubscription({ token } = {}) {
  if (!isPushSupported()) return { ok: false, reason: "PUSH_NOT_SUPPORTED" };
  let permission = Notification.permission;
  if (permission === "default") permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: permission === "denied" ? "NOTIFICATION_DENIED" : "NOTIFICATION_PERMISSION_NOT_GRANTED" };
  return ensureDevicePushSubscription({ token });
}

export async function autoEnsureDevicePushSubscription({ token } = {}) {
  const now = Date.now();
  if (now - lastAutoSubscribeAt < 10000) return { ok: false, reason: "THROTTLED" };
  lastAutoSubscribeAt = now;
  if (!isPushSupported()) return { ok: false, reason: "PUSH_NOT_SUPPORTED" };
  if (Notification.permission !== "granted") return { ok: false, reason: "NOTIFICATION_PERMISSION_NOT_GRANTED" };
  return ensureDevicePushSubscription({ token });
}

export async function resetDevicePushSubscription({ token } = {}) {
  if (!isPushSupported()) return { ok: false, reason: "PUSH_NOT_SUPPORTED" };
  const registration = await registerChatServiceWorker();
  try {
    const current = await registration.pushManager.getSubscription();
    if (current) await current.unsubscribe();
  } catch (err) {}
  try { await fetchJson("/reset", { token, method: "POST", body: {} }); } catch (err) {}
  return requestAndEnsureDevicePushSubscription({ token });
}

export async function getPushStatus({ token } = {}) {
  if (!isPushSupported()) return { ok: false, reason: "PUSH_NOT_SUPPORTED", browser_permission: "unsupported" };
  const server = await fetchJson("/status", { token });
  return { ...server, browser_permission: Notification.permission };
}

export async function sendPushTest({ token } = {}) {
  return fetchJson("/test", { token, method: "POST", body: {} });
}
